'use client'

import { useTranslations } from 'next-intl'
import { memo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { useUploadUrl } from '@/entities/storage'
import { ImageUpload } from '@/shared/lib'
import { Button, Form, Icon, Image } from '@/shared/ui'

import { TPassportVerificationForm } from '../../model/passport-verification-form'

export const PassportImageForm = memo(() => {
  const t = useTranslations('passport_verification')

  const { generateUploadUrl } = useUploadUrl()

  const {
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext<TPassportVerificationForm>()

  const passportImageUrl = useWatch({
    control,
    name: 'passportImageUrl',
  })

  const handleUploadPassportImage = async ({ file }: { file: File }) => {
    const url = await generateUploadUrl(file, {
      fileName: file.name,
      fileSize: file.size,
      prefix: 'registid',
    })

    if (!url) return

    if (errors.passportImageUrl) {
      clearErrors('passportImageUrl')
    }

    setValue('passportImageUrl', url)
  }

  return (
    <Form.Field name="passportImageUrl">
      <Form.Label className="typo-label1">
        {t('passport_photo_label')}
      </Form.Label>
      {passportImageUrl && (
        <div className="flex justify-center rounded-sm border border-gray-100 bg-white py-[11px]">
          <Image
            src={passportImageUrl}
            alt="여권"
            width={162}
            height={100}
            external={true}
            className="rounded-sm"
          />
        </div>
      )}
      <ImageUpload onChange={handleUploadPassportImage}>
        <Button fullWidth className="gap-2">
          {!passportImageUrl && <Icon name="plus" />}
          {passportImageUrl
            ? t('button.change_passport_photo')
            : t('button.upload_passport_photo')}
        </Button>
      </ImageUpload>
      <Form.Message />
    </Form.Field>
  )
})

PassportImageForm.displayName = 'PassportImageForm'
